import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Pagination,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';
import { ActivityTypes } from '../utils/activityLogger';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [adminNameFilter, setAdminNameFilter] = useState('');
  const [detailsFilter, setDetailsFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const rowsPerPage = 10;

  useEffect(() => {
    // Add debounce for text search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchActivities();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [page, actionFilter, filter, adminNameFilter, detailsFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionFilter, filter, adminNameFilter, detailsFilter]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.dashboard.getAdminActivities({
        page,
        limit: rowsPerPage,
        action: actionFilter,
        search: filter,
        adminName: adminNameFilter,
        details: detailsFilter
      });

      if (response && response.success) {
        setActivities(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        throw new Error(response?.message || 'Failed to fetch activities');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(err.message || 'Error fetching activities. Please try again later.');
      setActivities([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities;

  const getActivityColor = (action) => {
    const actionType = action?.toUpperCase();
    switch (actionType) {
      case ActivityTypes.CREATE:
      case ActivityTypes.APPROVE:
      case ActivityTypes.ENABLE:
      case ActivityTypes.ACTIVATE_USER:
      case ActivityTypes.FUND_USER_WALLET:
        return theme.palette.success.main;
      case ActivityTypes.DELETE:
      case ActivityTypes.REJECT:
      case ActivityTypes.DISABLE:
      case ActivityTypes.SUSPEND_USER:
        return theme.palette.error.main;
      case ActivityTypes.UPDATE:
      case ActivityTypes.EDIT:
      case ActivityTypes.SETTINGS:
        return theme.palette.warning.main;
      case ActivityTypes.VIEW:
      case ActivityTypes.SEARCH:
      case ActivityTypes.VIEW_USER_DETAILS:
      case ActivityTypes.NOTIFICATION_VIEW:
      case ActivityTypes.PAGE_VISIT:
        return theme.palette.info.main;
      case ActivityTypes.NOTIFICATION_MARK_VIEWED:
      case ActivityTypes.NOTIFICATION_MARK_ALL_VIEWED:
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getActionIcon = (action) => {
    const actionType = action?.toUpperCase();
    switch (actionType) {
      case ActivityTypes.CREATE:
        return <AddIcon />;
      case ActivityTypes.DELETE:
        return <DeleteIcon />;
      case ActivityTypes.UPDATE:
      case ActivityTypes.EDIT:
        return <EditIcon />;
      case ActivityTypes.VIEW:
      case ActivityTypes.VIEW_USER_DETAILS:
      case ActivityTypes.PAGE_VISIT:
        return <VisibilityIcon />;
      case ActivityTypes.APPROVE:
      case ActivityTypes.ENABLE:
      case ActivityTypes.ACTIVATE_USER:
        return <CheckCircleIcon />;
      case ActivityTypes.REJECT:
      case ActivityTypes.DISABLE:
      case ActivityTypes.SUSPEND_USER:
        return <CancelIcon />;
      case ActivityTypes.SETTINGS:
        return <SettingsIcon />;
      case ActivityTypes.NOTIFICATION_VIEW:
        return <NotificationsIcon />;
      case ActivityTypes.NOTIFICATION_MARK_VIEWED:
      case ActivityTypes.NOTIFICATION_MARK_ALL_VIEWED:
        return <NotificationsActiveIcon />;
      case ActivityTypes.SEARCH:
        return <SearchIcon />;
      case ActivityTypes.FUND_USER_WALLET:
        return <AccountBalanceWalletIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            color="primary"
            sx={{
              textShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
              letterSpacing: '-0.5px',
            }}
          >
            Admin Activities
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Track and monitor all administrative actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchActivities}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Filters">
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showFilters && (
        <Box sx={{
          mb: 3,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search activities..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              },
            }}
          />

          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter by Admin Name..."
            value={adminNameFilter}
            onChange={e => setAdminNameFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
              }
            }}
          />

          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter by Details..."
            value={detailsFilter}
            onChange={e => setDetailsFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
              }
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Action</InputLabel>
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              label="Filter by Action"
              sx={{
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {Object.values(ActivityTypes).map((type) => (
                <MenuItem key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <LoadingSpinner size="large" />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.1)}`,
          }}
        >
          {error}
        </Alert>
      ) : (
        <Box>
          <Paper
            sx={{
              width: '100%',
              overflow: 'auto',
              borderRadius: 3,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: theme => `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              '& .MuiTableHead-root': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                '& th': {
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap',
                  py: 2.5,
                  px: 3,
                  fontSize: '0.875rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                },
              },
              '& .MuiTableBody-root': {
                '& tr': {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
                  },
                  '& td': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 2.5,
                    px: 3,
                    fontSize: '0.875rem',
                  },
                },
              },
            }}
          >
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivities.length > 0 ? (
                    filteredActivities.map((activity, index) => (
                      <TableRow
                        key={activity._id}
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          animation: 'fadeIn 0.3s ease',
                          animationFillMode: 'both',
                          animationDelay: `${index * 0.05}s`,
                          '@keyframes fadeIn': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(10px)',
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0)',
                            },
                          },
                        }}
                      >
                        <TableCell>
                          <Tooltip
                            title={activity.action.replace(/_/g, ' ')}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: alpha(getActivityColor(activity.action), 0.1),
                                color: getActivityColor(activity.action),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: alpha(getActivityColor(activity.action), 0.2),
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              {getActionIcon(activity.action)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {activity.details}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: '0.875rem',
                              }}
                            >
                              {activity.adminName?.[0]?.toUpperCase() || 'A'}
                            </Box>
                            <Typography variant="body2" fontWeight={600}>
                              {activity.adminName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={new Date(activity.timestamp).toLocaleString()}
                            arrow
                            placement="top"
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2,
                          color: 'text.secondary'
                        }}>
                          <InfoIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.primary" gutterBottom>
                              No activities found
                            </Typography>
                            <Typography variant="body2">
                              Try adjusting your filters or search terms
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3} mb={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                    '&.Mui-selected': {
                      boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Activities; 